import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import {
  setEventCategoryCurrentPage,
  setEventCategoryPageLimit,
  setEventCategoryTotalCount,
  setEventCategoryTotalPages,
} from "../../../../lib/slices/pagination/eventCategoryPageSlice";
import { setEventCategory } from "../../../../lib/slices/searchKeysSlice";
import {
  getEventCategories,
  updateEventCategory,
} from "../../../../services/eventCategoryApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditEventCategoryDialog } from "./AddEditEventCategoryDialog";

const headerData = [
  "createdAt",
  "serialNumber",
  "name",
  "description",
  "status",
  "featured",
  "actions",
];

const tableRows = [
  "created_date",
  "serialNo",
  "event_category_name",
  "event_category_description",
  "status",
  "featured",
  "actions",
];

export function EventCategory() {
  const dispatch = useDispatch();
  const openDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [allEventCategories, setAllEventCategories] = useState([]);
  const { t } = useTranslation(["catalogue", "common"]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const pagination = useSelector(
    (state) => state.eventCategoryPaginationReducer,
  );
  const debouncedSearchTerm = useDebounce(
    searchKeys.eventCategory,
    1000,
    true,
    () => dispatch(setEventCategoryCurrentPage(1)),
  );

  const fetchEventCategories = async (search, page, limit, loading = true) => {
    try {
      setIsLoading(loading);
      const response = await getEventCategories({ search, page, limit });
      if (response?.data?.status === "success") {
        const result = response?.data?.data;
        setAllEventCategories(result?.data || []);
        if (result?.pagination) {
          dispatch(setEventCategoryPageLimit(result.pagination.limit));
          dispatch(setEventCategoryCurrentPage(result.pagination.page));
          dispatch(setEventCategoryTotalCount(result.pagination.total));
          dispatch(setEventCategoryTotalPages(result.pagination.totalPages));
        }
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEventCategories(
      debouncedSearchTerm,
      pagination.page,
      pagination.limit,
    );
  }, [debouncedSearchTerm, pagination.page, pagination.limit]);

  const handleChangeStatus = async (value, row, key) => {
    try {
      const response = await updateEventCategory(row._id, { [key]: value });
      if (response?.data?.status === "success") {
        fetchEventCategories(
          searchKeys.eventCategory,
          pagination.page,
          pagination.limit,
          false,
        );
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    }
  };

  const exportData = allEventCategories.map((item) => ({
    "Created At": moment(item.createdAt).format("MM-DD-YYYY"),
    "Serial Number": item.serialNo ?? "-",
    "Name In Romanian": item.name?.ro || "-",
    "Name In English": item.name?.en || "-",
    Status: item.status ? "Active" : "Inactive",
  }));

  const renderTable = useMemo(
    () => (
      <DynamicPaginatedTable
        minWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allEventCategories}
        showActions
        showEdit
        handleEdit={(params) =>
          openDialog.current.openDialog({
            type: "edit",
            data: params.row,
            getData: fetchEventCategories,
          })
        }
        handleChangeStatus={(value, row) =>
          handleChangeStatus(value, row, "status")
        }
        handleChangeFeatured={(value, row) =>
          handleChangeStatus(value, row, "isFeatured")
        }
        paginatedData={pagination}
        showDynamicPagination
        onPageChange={(_, page) =>
          dispatch(setEventCategoryCurrentPage(page + 1))
        }
        onRowsPerPageChange={(event) => {
          dispatch(setEventCategoryPageLimit(Number(event.target.value)));
          dispatch(setEventCategoryCurrentPage(1));
        }}
        rowsPerPageOptions={[10, 20, 30, 50, 100]}
        isLoading={isLoading}
      />
    ),
    [allEventCategories, isLoading],
  );

  return (
    <>
      <PaperContainer
        title={t("eventCategory")}
        subtitle={t("viewEventCategory")}
        headerActions={
          <Stack
            gap={1}
            flexGrow={1}
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
          >
            <GlobalButton
              variant="contained"
              btnText="addNew"
              handleClickBtn={() =>
                openDialog.current.openDialog({
                  type: "add",
                  data: null,
                  getData: fetchEventCategories,
                })
              }
              styleProps={{ width: "auto", height: 40 }}
            />
            <CSVLink
              data={exportData}
              filename="event-categories.csv"
              className="csv-link"
              target="_blank"
            >
              <GlobalButton
                variant="export_button"
                btnText="export"
                startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                styleProps={{ width: "auto", height: 40 }}
              />
            </CSVLink>
          </Stack>
        }
        searchActions={
          <Stack p={2} direction={{ md: "row" }} bgcolor="primary.light">
            <TextInput
              styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
              InputStartIcon={
                <img src={APP_ICONS.searchGreyIcon} width={25} />
              }
              InputEndIcon={
                searchKeys.eventCategory && (
                  <CancelOutlinedIcon
                    fontSize="small"
                    sx={{ cursor: "pointer" }}
                    onClick={() => dispatch(setEventCategory(""))}
                  />
                )
              }
              placeholder={t("searchByName", { ns: "common" })}
              value={searchKeys.eventCategory}
              onChange={(event) =>
                dispatch(setEventCategory(event.target.value))
              }
            />
          </Stack>
        }
      >
        {renderTable}
      </PaperContainer>
      <AddEditEventCategoryDialog ref={openDialog} />
    </>
  );
}
